/**
 * Saved studios on `/login` — the per-device quick-login list
 * (staff-person-login-s2b, 2026-09-27).
 *
 * WHY. Mario's ask (2026-09-27): "if I'm locked out or if I log out from one
 * dashboard, I can still see the studios I logged in to previously. I could
 * just click on one, and then the code is sent to the corresponding number or
 * email." A lost or signed-out session is exactly when a person has nothing
 * to identify themselves with except memory; the list gives them their own
 * studios back as one tap each.
 *
 * WHAT IS STORED, and why it is safe to store: the person's login address
 * (the thing they would type into the box anyway) and the `{slug, name}` of
 * each studio `/api/whoami` said they belong to, plus a last-used time. No
 * token, no code, no phone, nothing that grants access — tapping a card still
 * sends the 8-digit code to the address or number on the allowlist row, and
 * the code is still required. It is `localStorage`, so per browser and per
 * origin (which is why S2b folds the two hosts into one), and it survives a
 * sign-out on purpose: `supabase.auth.signOut` removes its own key only.
 *
 * The layout writes the list after EVERY successful whoami (the studios come
 * from the server, never from the person), capped at `SAVED_LOGINS_MAX`
 * addresses by recency; each card can be forgotten from the login page. Every
 * storage access is guarded — `localStorage` throws in some contexts, and a
 * malformed value must read as "nothing saved", never as a crash on the
 * login page of all pages.
 */

export const SAVED_LOGINS_KEY = "layer.saved_logins";
export const SAVED_LOGINS_MAX = 8;

export interface SavedStudio {
  readonly slug: string;
  readonly name: string;
}

export interface SavedLogin {
  /** The login address, lower-cased and trimmed — what `/api/login-link` is sent. */
  readonly identifier: string;
  readonly studios: ReadonlyArray<SavedStudio>;
  /** ISO timestamp of the last successful whoami for this address on this device. */
  readonly last_used_at: string;
}

/** One card on the login page: a (person, studio) pair. */
export interface SavedStudioCard {
  readonly identifier: string;
  readonly slug: string;
  readonly name: string;
  readonly last_used_at: string;
}

/** The subset of `Storage` used, so tests inject a plain object. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `window.localStorage` when it can be touched, else null (never throws). */
export function browserStorage(): StorageLike | null {
  try {
    if (typeof window === "undefined") return null;
    const s = window.localStorage;
    // Some contexts expose the property and throw on use.
    s.getItem(SAVED_LOGINS_KEY);
    return s;
  } catch {
    return null;
  }
}

const SLUG_SHAPE = /^[a-z0-9][a-z0-9-]*$/;

function normalizeIdentifier(raw: string): string {
  return raw.trim().toLowerCase();
}

function isSavedStudio(v: unknown): v is SavedStudio {
  if (typeof v !== "object" || v === null) return false;
  const o = v as { slug?: unknown; name?: unknown };
  return (
    typeof o.slug === "string" &&
    SLUG_SHAPE.test(o.slug) &&
    typeof o.name === "string" &&
    o.name.trim().length > 0
  );
}

function isSavedLogin(v: unknown): v is SavedLogin {
  if (typeof v !== "object" || v === null) return false;
  const o = v as { identifier?: unknown; studios?: unknown; last_used_at?: unknown };
  return (
    typeof o.identifier === "string" &&
    o.identifier.trim().length > 0 &&
    Array.isArray(o.studios) &&
    o.studios.length > 0 &&
    o.studios.every(isSavedStudio) &&
    typeof o.last_used_at === "string" &&
    !Number.isNaN(Date.parse(o.last_used_at))
  );
}

function byRecency(a: SavedLogin, b: SavedLogin): number {
  return Date.parse(b.last_used_at) - Date.parse(a.last_used_at);
}

/** The saved list, most recently used first. Malformed or unreadable storage = `[]`. */
export function readSavedLogins(storage: StorageLike | null | undefined): SavedLogin[] {
  if (storage === null || storage === undefined) return [];
  try {
    const raw = storage.getItem(SAVED_LOGINS_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedLogin).sort(byRecency).slice(0, SAVED_LOGINS_MAX);
  } catch {
    return [];
  }
}

function write(storage: StorageLike, list: ReadonlyArray<SavedLogin>): void {
  try {
    if (list.length === 0) storage.removeItem(SAVED_LOGINS_KEY);
    else storage.setItem(SAVED_LOGINS_KEY, JSON.stringify(list));
  } catch {
    // Quota, private mode, a locked-down browser — the list is a convenience.
  }
}

/**
 * Record a successful sign-in: the address's entry is created or refreshed
 * with the studios the SERVER just listed (replacing, not merging — a studio
 * the person was removed from must disappear), stamped `nowIso`, and the
 * list is re-capped by recency. Returns the new list. A person with no
 * studios is not recorded (there is nothing to open).
 */
export function rememberLogin(
  storage: StorageLike | null | undefined,
  login: { readonly identifier: string; readonly studios: ReadonlyArray<SavedStudio> },
  nowIso: string,
): SavedLogin[] {
  const identifier = normalizeIdentifier(login.identifier);
  const studios = login.studios.filter(isSavedStudio).map((s) => ({ slug: s.slug, name: s.name }));
  const current = readSavedLogins(storage);
  if (identifier.length === 0 || studios.length === 0) return current;
  const entry: SavedLogin = { identifier, studios, last_used_at: nowIso };
  const next = [entry, ...current.filter((l) => l.identifier !== identifier)]
    .sort(byRecency)
    .slice(0, SAVED_LOGINS_MAX);
  if (storage !== null && storage !== undefined) write(storage, next);
  return next;
}

/**
 * Forget ONE card. The address's entry goes when its last studio does.
 * Returns the new list.
 */
export function forgetSavedStudio(
  storage: StorageLike | null | undefined,
  identifier: string,
  slug: string,
): SavedLogin[] {
  const id = normalizeIdentifier(identifier);
  const next = readSavedLogins(storage)
    .map((l) =>
      l.identifier === id ? { ...l, studios: l.studios.filter((s) => s.slug !== slug) } : l,
    )
    .filter((l) => l.studios.length > 0);
  if (storage !== null && storage !== undefined) write(storage, next);
  return next;
}

/** The cards to render: most recent address first, its studios in stored order. */
export function savedStudioCards(list: ReadonlyArray<SavedLogin>): SavedStudioCard[] {
  const out: SavedStudioCard[] = [];
  for (const l of list) {
    for (const s of l.studios) {
      out.push({ identifier: l.identifier, slug: s.slug, name: s.name, last_used_at: l.last_used_at });
    }
  }
  return out;
}
